import {
    Badge,
    Box,
    Button,
    Checkbox,
    Group,
    Modal,
    ScrollArea,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';

export interface FilterItem {
    value: string;
    label: string;
    count: number;
    isRefined: boolean;
}

export interface FacetFilterSection {
    id: string;
    title: string;
    items: FilterItem[];
    refine: (value: string) => void;
    renderLabel?: (item: FilterItem) => ReactNode;
}

export interface CustomFilterSection {
    id: string;
    title: string;
    type: 'custom';
    selectedCount?: number;
    onClear?: () => void;
    renderContent: () => ReactNode;
}

export type FilterSection = FacetFilterSection | CustomFilterSection;

interface FilterModalProps {
    opened: boolean;
    onClose: () => void;
    sections: FilterSection[];
}

interface KnownFacetOption {
    value: string;
    label: string;
}

type KnownOptionsBySection = Record<string, Record<string, KnownFacetOption>>;
const countFormatter = new Intl.NumberFormat();

const isCustomSection = (section: FilterSection): section is CustomFilterSection => {
    return 'type' in section && section.type === 'custom';
};

const isFacetSection = (section: FilterSection): section is FacetFilterSection => {
    return !isCustomSection(section);
};

const mergeSectionItems = (
    section: FacetFilterSection,
    knownSectionItems: Record<string, KnownFacetOption> | undefined
): FilterItem[] => {
    const liveByValue = new Map(section.items.map((item) => [item.value, item]));
    const knownItems = Object.values(knownSectionItems ?? {});
    const mergedItems = knownItems.map((knownItem) => {
        const liveItem = liveByValue.get(knownItem.value);
        if (liveItem) {
            return liveItem;
        }

        return {
            ...knownItem,
            count: 0,
            isRefined: false,
        };
    });

    const knownValues = new Set(knownItems.map((item) => item.value));
    section.items.forEach((item) => {
        if (!knownValues.has(item.value)) {
            mergedItems.push(item);
        }
    });

    return mergedItems;
};

const FilterModal: FC<FilterModalProps> = ({
    opened,
    onClose,
    sections,
}) => {
    const [sectionSearchTerms, setSectionSearchTerms] = useState<Record<string, string>>({});
    const [knownItemsBySection, setKnownItemsBySection] = useState<KnownOptionsBySection>({});

    useEffect(() => {
        if (opened) {
            setSectionSearchTerms({});
        }
    }, [opened]);

    useEffect(() => {
        setKnownItemsBySection((prev) => {
            const next: KnownOptionsBySection = { ...prev };
            const facetSections = sections.filter(isFacetSection);
            const sectionIds = new Set(facetSections.map((section) => section.id));
            let hasChanges = false;

            Object.keys(next).forEach((sectionId) => {
                if (!sectionIds.has(sectionId)) {
                    delete next[sectionId];
                    hasChanges = true;
                }
            });

            facetSections.forEach((section) => {
                const existingSection = next[section.id] ? { ...next[section.id] } : {};
                let sectionChanged = !next[section.id];

                section.items.forEach((item) => {
                    const existingItem = existingSection[item.value];
                    if (
                        !existingItem ||
                        existingItem.label !== item.label
                    ) {
                        existingSection[item.value] = {
                            value: item.value,
                            label: item.label,
                        };
                        sectionChanged = true;
                    }
                });

                if (sectionChanged) {
                    next[section.id] = existingSection;
                    hasChanges = true;
                }
            });

            return hasChanges ? next : prev;
        });
    }, [sections]);

    const sectionViewModels = useMemo(() => {
        return sections.map((section) => {
            if (isCustomSection(section)) {
                return section;
            }

            const mergedItems = mergeSectionItems(section, knownItemsBySection[section.id]);
            return {
                ...section,
                mergedItems,
            };
        });
    }, [sections, knownItemsBySection]);

    const totalSelectedCount = useMemo(() => {
        return sectionViewModels.reduce((total, section) => {
            if (isCustomSection(section)) {
                return total + (section.selectedCount ?? 0);
            }

            return total + section.mergedItems.filter((item) => item.isRefined).length;
        }, 0);
    }, [sectionViewModels]);

    const handleSectionSearchChange = (sectionId: string, value: string) => {
        setSectionSearchTerms((prev) => ({
            ...prev,
            [sectionId]: value,
        }));
    };

    const handleToggleSelection = (section: FilterSection, item: FilterItem) => {
        if (isCustomSection(section)) {
            return;
        }

        section.refine(item.value);
    };

    const handleClearAll = () => {
        const refinedItems = sectionViewModels.flatMap((section) =>
            isCustomSection(section)
                ? []
                : section.mergedItems
                    .filter((item) => item.isRefined)
                    .map((item) => ({
                        value: item.value,
                        refine: section.refine,
                    }))
        );

        if (refinedItems.length === 0) {
            const hasCustomSelections = sectionViewModels.some(
                (section) => isCustomSection(section) && (section.selectedCount ?? 0) > 0
            );

            if (!hasCustomSelections) {
                return;
            }
        }

        refinedItems.forEach(({ refine, value }) => {
            refine(value);
        });

        sectionViewModels.forEach((section) => {
            if (isCustomSection(section) && (section.selectedCount ?? 0) > 0) {
                section.onClear?.();
            }
        });
    };

    return (
        <Modal
            size={1100}
            opened={opened}
            onClose={onClose}
            title="Filters"
            centered
        >
            <Stack gap="sm">
                <Group justify="space-between">
                    <Group gap="xs">
                        <Badge color="blue" variant="light">
                            {totalSelectedCount} selected
                        </Badge>
                    </Group>
                    <Button
                        size="xs"
                        variant="subtle"
                        onClick={handleClearAll}
                        disabled={totalSelectedCount === 0}
                    >
                        Clear all
                    </Button>
                </Group>

                <ScrollArea.Autosize mah="66vh" offsetScrollbars>
                    <Stack gap="sm">
                        {sectionViewModels.map((section) => {
                            if (isCustomSection(section)) {
                                const selectedCount = section.selectedCount ?? 0;

                                return (
                                    <Box
                                        key={section.id}
                                        p="sm"
                                        style={{
                                            border: '1px solid var(--mantine-color-gray-3)',
                                            borderRadius: '10px',
                                        }}
                                    >
                                        <Stack gap={8}>
                                            <Group justify="space-between" wrap="nowrap">
                                                <Group gap="xs">
                                                    <Text fw={700}>{section.title}</Text>
                                                    {selectedCount > 0 ? (
                                                        <Badge color="green" variant="light" size="sm">
                                                            {selectedCount}
                                                        </Badge>
                                                    ) : null}
                                                </Group>
                                                {section.onClear ? (
                                                    <Button
                                                        size="xs"
                                                        variant="subtle"
                                                        onClick={section.onClear}
                                                        disabled={selectedCount === 0}
                                                    >
                                                        Clear
                                                    </Button>
                                                ) : null}
                                            </Group>

                                            {section.renderContent()}
                                        </Stack>
                                    </Box>
                                );
                            }

                            const selectedCount = section.mergedItems.filter((item) => item.isRefined).length;
                            const query = (sectionSearchTerms[section.id] ?? '').trim().toLowerCase();
                            const shouldShowSearch = section.mergedItems.length > 8;

                            const sortedItems = [...section.mergedItems].sort((a, b) => {
                                const labelOrder = a.label.localeCompare(b.label, undefined, {
                                    sensitivity: 'base',
                                });
                                if (labelOrder !== 0) {
                                    return labelOrder;
                                }
                                return b.count - a.count;
                            });

                            const visibleItems = query
                                ? sortedItems.filter((item) => {
                                    return (
                                        item.label.toLowerCase().includes(query) ||
                                        item.value.toLowerCase().includes(query)
                                    );
                                })
                                : sortedItems;

                            return (
                                <Box
                                    key={section.id}
                                    p="sm"
                                    style={{
                                        border: '1px solid var(--mantine-color-gray-3)',
                                        borderRadius: '10px',
                                    }}
                                >
                                    <Stack gap={8}>
                                        <Group justify="space-between" wrap="nowrap">
                                            <Group gap="xs">
                                                <Text fw={700}>{section.title}</Text>
                                                {selectedCount > 0 ? (
                                                    <Badge color="green" variant="light" size="sm">
                                                        {selectedCount}
                                                    </Badge>
                                                ) : null}
                                            </Group>
                                            <Text size="xs" c="dimmed">
                                                {visibleItems.length}/{section.mergedItems.length}
                                            </Text>
                                        </Group>

                                        {shouldShowSearch ? (
                                            <TextInput
                                                size="sm"
                                                placeholder="Search options"
                                                value={sectionSearchTerms[section.id] ?? ''}
                                                onChange={(event) =>
                                                    handleSectionSearchChange(
                                                        section.id,
                                                        event.currentTarget.value
                                                    )
                                                }
                                            />
                                        ) : null}

                                        <ScrollArea.Autosize
                                            mah={section.mergedItems.length > 10 ? 220 : 140}
                                            offsetScrollbars
                                        >
                                            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xs" verticalSpacing={6}>
                                                {visibleItems.map((item) => (
                                                    <Checkbox
                                                        key={`${section.id}-${item.value}`}
                                                        color="green"
                                                        checked={item.isRefined}
                                                        disabled={item.count === 0 && !item.isRefined}
                                                        onChange={() => handleToggleSelection(section, item)}
                                                        value={item.value}
                                                        label={
                                                            <Group gap={8} wrap="nowrap" justify="space-between">
                                                                <span>
                                                                    {section.renderLabel ? section.renderLabel(item) : item.label}
                                                                </span>
                                                                <Badge
                                                                    size="xs"
                                                                    variant={item.count > 0 ? 'light' : 'default'}
                                                                    color={item.count > 0 ? 'blue' : 'gray'}
                                                                >
                                                                    {countFormatter.format(item.count)}
                                                                </Badge>
                                                            </Group>
                                                        }
                                                    />
                                                ))}
                                            </SimpleGrid>
                                        </ScrollArea.Autosize>

                                        {visibleItems.length === 0 ? (
                                            <Text size="sm" c="dimmed">
                                                No matching options
                                            </Text>
                                        ) : null}
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </ScrollArea.Autosize>

                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        Close
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default FilterModal;
